// AdminAttendanceNoticesStrip — calm read-only strip of today's
// parent-submitted absence/late notices. Renders inline on
// /admin/attendance between the KPIs and the CSV export tools.
//
// Hides itself completely when there are zero submitted notices for the
// selected date (operator clarity > zero state noise).

import { useEffect, useState } from "react";
import { Clock, ProhibitInset } from "@phosphor-icons/react";
import {
  formatLocalTime,
  listOrgNoticesForDate,
  NOTICE_LABELS,
} from "@/lib/attendanceNotices";
import { isSupabaseConfigured } from "@/lib/supabase";

export default function AdminAttendanceNoticesStrip({ date }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    listOrgNoticesForDate(date)
      .then((rows) => {
        if (alive) setNotices(rows || []);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [date]);

  if (loading) return null;
  if (notices.length === 0) return null;

  const counts = notices.reduce(
    (acc, n) => {
      acc[n.notice_type] = (acc[n.notice_type] || 0) + 1;
      return acc;
    },
    { absent: 0, late: 0 },
  );

  return (
    <section
      data-testid="admin-attendance-notices-strip"
      className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-800">
          Parent notices · {date}
        </p>
        <p className="text-[11px] text-amber-900/80">
          {counts.absent} absent · {counts.late} late
        </p>
      </div>
      <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {notices.map((n) => (
          <li
            key={n.id}
            data-testid={`admin-notice-${n.id}`}
            className="flex items-start gap-2 rounded-2xl border border-amber-200/80 bg-white/70 p-3"
          >
            {n.notice_type === "absent" ? (
              <ProhibitInset
                size={16}
                weight="duotone"
                className="mt-0.5 text-[#8A5A12]"
                aria-hidden
              />
            ) : (
              <Clock
                size={16}
                weight="duotone"
                className="mt-0.5 text-amber-700"
                aria-hidden
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-neutral-900">
                {n.child?.first_name} {n.child?.last_name || ""} ·{" "}
                {NOTICE_LABELS[n.notice_type]}
                {n.notice_type === "late" && n.expected_arrival_time
                  ? ` · ~${formatLocalTime(n.expected_arrival_time)}`
                  : ""}
              </p>
              <p className="mt-0.5 text-[12px] text-neutral-600">
                {n.classroom?.name || "Unassigned"}
                {n.family?.display_name ? ` · ${n.family.display_name}` : ""}
                {n.submitter?.full_name
                  ? ` · by ${n.submitter.full_name}`
                  : ""}
              </p>
              {n.reason && (
                <p className="mt-1 text-[12px] italic text-neutral-700">
                  "{n.reason}"
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
